import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import {formatMoney, stringComparator} from "../../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Tab, Tabs} from "../../../../../../components/base/Tabs";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ArrowBack, FileCopyOutlined, MailOutline, OpenInNew, SettingsBackupRestore} from "@mui/icons-material";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import Button from "../../../../../../components/base/button/button";
import Api from "../../../../../../../core/services/api/api";
import ToastService from "../../../../../../../core/services/toast";
import OrderInformationViewEditAddressesSection from "../../../../../../components/app-specific/order-inforamtion-shared-sections/edit-addresses";
import {CustomerAddressTypes} from "../../../../../../../core/constants/enums";
import OrderInformationViewTransactionsSection, {OrderInformationViewTransactionsTypes} from "../../../../../../components/app-specific/order-inforamtion-shared-sections/transactions";
import BackOrderGenerationConfirmationDialog from "../../../../../../components/dialogs/backorder-generation-confirmation";
import {ReactComponent as EmptyCart} from '../../../../../../../assets/images/art-like/empty_cart.svg';
import SendOrderEmailDialog from "../../../../../../components/dialogs/send-order-email";
import OrderInformationViewNotesSection, {OrderNoteAutoDefineTypes} from "../../../../../../components/app-specific/order-inforamtion-shared-sections/notes";
import ApiResponseCodes from "../../../../../../../core/models/static/api-response-codes";
import OrderInformationViewShippingInfoCard from "../../../../../../components/app-specific/order-inforamtion-shared-sections/shipping-info-card/shipping-information";
import {Box, CircularProgress, SvgIcon, Tooltip, Typography} from "@mui/material";
import SummaryInfoCard, {summaryInfoCardBodyEntryTypes} from "../../../../../../components/app-specific/summary-cards/summary-info-card";
import OrderInformationViewReadOnlyAddressesSection from "../../../../../../components/app-specific/order-inforamtion-shared-sections/read-only-addresses";
import SalesOrderInformationViewBillInfoSection from "./sections/bill-info";
import SalesOrderInformationViewProductsSection from "./sections/products";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../../core/services/date-utils";
import useCachedPriceLevelsDictionaryController from "../../../../../../hooks/use-price-level-dictionary";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {ReactComponent as Printer} from '../../../../../../../assets/images/printer.svg'
import {Link, useHistory, useParams} from "react-router-dom";
import useQuery from "../../../../../../hooks/use-query";
import {useAppSelector} from "../../../../../../redux";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import PrintOrderConfigurationsDialog from "../../../../../../components/dialogs/print-order-configurations";
import {Popover} from "../../../../../../components/app-specific/popover";
import {stringifyUrl} from "query-string";
import UpsertOrderInfoDialog from "../../../../../../components/dialogs/upsert-order-info";
import OrderDimensionsSection from "../../../../../../components/app-specific/order-inforamtion-shared-sections/dimensions";
import DuplicateOrderDialog, {DuplicateOrderVariants} from "../../../../../../components/dialogs/duplicate-order-dialog";
import EnvService from "../../../../../../../core/services/env";


const tabs = {
    products: 'products',
    shippingAddress: 'shippingAddress',
    billingAddress: 'billingAddress',
    addresses: 'addresses',
    transactions: 'transactions',
    dimensions: 'dimensions',
    notes: 'notes',
}

const orderInfoSummaryBodyIds = {
    invoice: 'invoice',
    termsCode: 'termsCode',
    salesPerson: 'salesPerson',
    accountManager: 'accountManager',
    territory: 'territory',
    orderDate: 'orderDate',
    type: 'type',
    status: 'status',
    poNo: 'poNo',
    fob: 'fob',
    requiredDate: 'requiredDate',
    referenceNo: 'referenceNo',
}

const userInfoSummaryBodyIds = {
    accountReceivable: 'accountReceivable',
    limitedCredit: 'limitedCredit',
    otherOrders: 'otherOrders',
    availableCredit: 'availableCredit',
    priceLevel: 'priceLevel',
    paymentTerms: 'paymentTerms',
}

const defaultTab = tabs.products

const initSendOrderEmailDialogState = {open: false};
const initPrintOrderConfigurationsDialogState = {open: false, printUrl: '', onClose: () => void 0};
const initUpsertOrderInfoDialogState = {open: false, entityToShow: ''};

const SalesOrderInformationView = () => {
    const params = useParams();
    const [query, setQuery] = useQuery();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [submittingDisabled, setSubmittingDisabled] = useState(false);
    const [order, _setOrder] = useState({});
    const [accountInfoLoading, setAccountInfoLoading] = useState(true);
    const [accountInfo, setAccountInfo] = useState({});
    const [reverting, setReverting] = useState(false);
    const [failedPreAuthPayments, setFailedPreAuthPayments] = useState([]);
    const [sendOrderEmailDialogState, setSendOrderEmailDialogState] = useState(initSendOrderEmailDialogState);
    const [attemptedInvoiceGeneration, setAttemptedInvoiceGeneration] = useState(false);
    const [openBackorderConfirmationDialog, setOpenBackorderConfirmationDialog] = useState(false);
    const [shippingInfo, setShippingInfo] = useState({});
    const [freeShippingEligibility, setFreeShippingEligibility] = useState({});
    const [freightEstimation, setFreightEstimation] = useState(null);
    const [loadingFreightEstimation, setLoadingFreightEstimation] = useState(false);
    const [loadingShippingInfo, setLoadingShippingInfo] = useState(true);
    const [printOrderConfigurationsDialog, setPrintOrderConfigurationsDialog] = useState(initPrintOrderConfigurationsDialogState);
    const [upsertOrderInfoDialog, setUpsertOrderInfoDialog] = useState(initUpsertOrderInfoDialogState);
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const isMounted = useIsMounted();
    const shippingInfoCompanyIdRef = React.useRef();

    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const {configureNotebookTags} = useConfigureNotebook('sales-orders-information-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();
    const operatorAccessLevels = useAccessLevels();


    const tabsList = useMemo(() => {
        const res = [
            {label: "Products", value: tabs.products},
            {label: "Addresses", value: tabs.addresses},
            {label: "Shipping Addresses", value: tabs.shippingAddress},
            {label: "Billing Addresses", value: tabs.billingAddress},
            {label: "Transactions", value: tabs.transactions},
            {label: "Notes", value: tabs.notes},
            {label: "Dimensions", value: tabs.dimensions},
        ];
        if (order?.isClosed || !operatorAccessLevels.packages.sales.manageSalesOrders) {
            res.splice(res.indexOf(res.find(e => e.value === tabs.shippingAddress)), 1);
            res.splice(res.indexOf(res.find(e => e.value === tabs.billingAddress)), 1);
        } else {
            res.splice(res.indexOf(res.find(e => e.value === tabs.addresses)), 1);
        }
        if (!operatorAccessLevels.packages.sales.salesPallets) {
            res.splice(res.indexOf(res.find(e => e.value === tabs.dimensions)), 1);
        }
        return res;
    }, [operatorAccessLevels.packages.sales.manageSalesOrders, operatorAccessLevels.packages.sales.salesPallets, order?.isClosed])

    const orderId = useMemo(() => Number(params.orderId), [params.orderId])
    const customerId = useMemo(() => Number(params.customerId), [params.customerId])

    const tab = useMemo(() => {
        let selected = query?.tab;
        if (!selected || !tabsList?.map(e => e.value).includes(selected)) {
            selected = defaultTab;
        }
        return selected;
    }, [query?.tab, tabsList])

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool
     */
    useEffect(() => {
        if (!defaultCustomer?.code)
            return;
        configureNotebookTags(
            (tags) => tags.customerSalesSalesOrderInformation(defaultCustomer?.code, orderId),
            (tags) => tags.customerSalesQuoteInformation(defaultCustomer?.code, orderId),
        );
        configureBehavioralTool(tags => tags.customerSalesSalesOrderInformation(defaultCustomer?.code, orderId));
    }, [orderId, configureNotebookTags, defaultCustomer?.code, configureBehavioralTool]);

    /**
     * Fetches the free shipping eligibility of the order from the server.
     * @type {function(*=): Promise<void>}
     */
    const getFreeShippingEligibility = useCallback(async (freightCompanyId = undefined) => {
        if (!operatorAccessLevels.packages.sales.orderFreightInfo) {
            return;
        }
        const response = await Api.calculateFreeFreightEligibility({
            orderId: orderId,
            freightCompanyId: freightCompanyId ?? shippingInfoCompanyIdRef.current,
        })
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setFreeShippingEligibility(response.data);
            setShippingInfo(prevState => ({...prevState, freeFreightId: response.data.id}));
        } else {
            setFreeShippingEligibility({});
        }
    }, [isMounted, operatorAccessLevels.packages.sales.orderFreightInfo, orderId])

    /**
     * Fetches the estimated freight fee for the order based on freight company and shipping address.
     * @type {function(number=): Promise<void>}
     */
    const getFreightEstimation = useCallback(async (freightCompanyId = undefined) => {
        if (!operatorAccessLevels.packages.sales.orderFreightInfo)
            return;
        const resolvedFreightCompanyId = freightCompanyId ?? shippingInfoCompanyIdRef.current;
        if (!resolvedFreightCompanyId) {
            setFreightEstimation(null);
            return;
        }
        setLoadingFreightEstimation(true);
        const response = await Api.estimateShippingFee({
            orderId: orderId,
            freightCompanyId: resolvedFreightCompanyId,
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setFreightEstimation(response.data);
        } else {
            setFreightEstimation(null);
        }
        setLoadingFreightEstimation(false);
    }, [isMounted, operatorAccessLevels.packages.sales.orderFreightInfo, orderId])

    /**
     * Sets the order state with its transactions sorted.
     * @type {function(*=): void}
     */
    const setOrder = useCallback((stateOrFunctionWithPrevState) => {
        _setOrder(prevState => {
            const newState = typeof stateOrFunctionWithPrevState === "function" ? stateOrFunctionWithPrevState(prevState) : stateOrFunctionWithPrevState;
            // sort the appliedTransactions by their type of the id so that the gift card can be at the beginning
            // or at the end.
            newState.appliedTransactions?.sort((a, b) => stringComparator(a.transactionTypeName, b.transactionTypeName));
            if (shippingInfoCompanyIdRef.current) {
                void getFreeShippingEligibility(shippingInfoCompanyIdRef.current);
                void getFreightEstimation(shippingInfoCompanyIdRef.current);
            }
            return {...newState}
        });
    }, [getFreeShippingEligibility, getFreightEstimation])

    /**
     * Fetches the customer account information from the server.
     * @type {function(): Promise<void>}
     */
    const getCustomerAccountInformation = useCallback(async () => {
        setAccountInfoLoading(true);
        const [response, priceLevelDict] = await Promise.all([
            Api.getCustomerAccountInformation(customerId),
            getPriceLevelDictionary(),
        ]);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setAccountInfo({
                ...response.data,
                priceLevel: {
                    ...response.data.priceLevel,
                    title: priceLevelDict?.find(e => e.priceLevelId === response.data.priceLevel.id)?.title ?? response.data.priceLevel.title,
                },
            })
        }
        setAccountInfoLoading(false);
    }, [getPriceLevelDictionary, isMounted, customerId])

    /**
     * As soon as the component is mounted:
     * - fetches user account information from the server.
     */
    useEffect(() => {
        void getCustomerAccountInformation();
    }, [getCustomerAccountInformation])

    /**
     * Fetches the order's shipping information from the server.
     * @type {function(*=): Promise<void>}
     */
    const getOrderShippingInformation = useCallback(async (orderInfo) => {
        if (!operatorAccessLevels.packages.sales.orderFreightInfo) {
            setLoadingShippingInfo(false);
            return;
        }
        setLoadingShippingInfo(true);
        const response = await Api.getFreightOrderInformation({orderId: orderId});
        if (!isMounted())
            return;
        setShippingInfo({
            id: response?.data?.id,
            freeFreightId: response?.data?.freeFreightRequestId,
            freightCompany: {
                id: response?.data?.freightCompanyId,
                title: response?.data?.freightCompanyTitle,
                code: response?.data?.freightCompanyCode ?? orderInfo?.freightCompanyCode
            },
            estimatedDate: response?.data?.estimatedDeliveryDateTime ?? orderInfo?.shipDate,
            estimatedFee: response?.data?.estimatedFee,
            customerFee: response?.data?.customerFee ?? orderInfo?.freightFee,
            trackingNo: response?.data?.trackingNumber ?? orderInfo?.trackingNo,
            shippingBillTo: response?.data?.shippingBillTo ?? null,
            metaData: response?.data?.metaData ?? null,
        });
        shippingInfoCompanyIdRef.current = response?.data?.freightCompanyId;
        if (response?.resultFlag && response.data?.freightCompanyId) {
            await Promise.all([
                getFreeShippingEligibility(response.data.freightCompanyId),
                getFreightEstimation(response.data.freightCompanyId),
            ]);
        } else {
            setFreightEstimation(null);
        }
        setLoadingShippingInfo(false);
    }, [isMounted, orderId, operatorAccessLevels.packages.sales.orderFreightInfo, getFreeShippingEligibility, getFreightEstimation])


    /**
     * Fetches the full information of the current order from the server.
     * - if the response is successful, sets the order in the state
     * @type {function(*=): Promise<void>}
     */
    const getOrderInformation = useCallback(async (setLoadingState = false) => {
        if (setLoadingState)
            setLoading(true);
        const response = await Api.getSalesOrderInformationForCustomer(customerId, orderId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOrder(response.data);
            void getOrderShippingInformation(response?.data);
        }
        setLoading(false);
    }, [customerId, orderId, isMounted, getOrderShippingInformation, setOrder])


    /**
     * With each change in the [customerId] or the [orderId] of the url params:
     * - fetches user account information and order information from the server.
     */
    useEffect(() => {
        void getOrderInformation()
    }, [getOrderInformation])

    /**
     * Updates the order's information.
     *
     * - if the result of the api is successful, sets the new information.
     * @type {function(*, *): Promise<boolean|undefined>}
     */
    const updateOrderInfo = useCallback(async (entry, newValue) => {
        let name;
        switch (entry.id) {
            case orderInfoSummaryBodyIds.poNo:
                name = 'customerPO';
                break;
            case orderInfoSummaryBodyIds.fob:
                name = 'fob';
                break;
            case orderInfoSummaryBodyIds.requiredDate:
                name = 'requiredDate';
                break;
            case orderInfoSummaryBodyIds.referenceNo:
                name = 'referenceNo';
                break;
            case orderInfoSummaryBodyIds.territory: {
                name = 'territory';
                let abort = false;
                [newValue, abort] = await new Promise(resolve => {
                    setUpsertOrderInfoDialog({
                        open: true,
                        entityToShow: 'territory',
                        territory: order.territory ? {code: order.territory} : null,
                        close: () => {
                            resolve([undefined, true])
                            setUpsertOrderInfoDialog(initUpsertOrderInfoDialogState)
                        },
                        update: (values) => {
                            resolve([values.territory?.code, false])
                        },
                    })
                })
                if (abort)
                    return true;
                break;
            }
            case orderInfoSummaryBodyIds.accountManager: {
                name = 'accountManager';
                let abort = false;
                [newValue, abort] = await new Promise(resolve => {
                    setUpsertOrderInfoDialog({
                        open: true,
                        entityToShow: 'accountManager',
                        accountManager: order.accountManager,
                        close: () => {
                            resolve([undefined, true])
                            setUpsertOrderInfoDialog(initUpsertOrderInfoDialogState)
                        },
                        update: (values) => resolve([values.accountManager, false]),
                    })
                })
                if (abort)
                    return true;
                break;
            }
            case orderInfoSummaryBodyIds.termsCode: {
                name = 'termsCode';
                let abort = false;
                [newValue, abort] = await new Promise(resolve => {
                    setUpsertOrderInfoDialog({
                        open: true,
                        entityToShow: 'termsCode',
                        termsCode: order.termsCode,
                        close: () => {
                            resolve([undefined, true])
                            setUpsertOrderInfoDialog(initUpsertOrderInfoDialogState)
                        },
                        update: (values) => resolve([values.termsCode, false]),
                    })
                })
                if (abort)
                    return true;
                break;
            }
            default:
                return;
        }
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            customerPo: entry.id === orderInfoSummaryBodyIds.poNo ? newValue ?? '' : order.customerPO,
            fob: entry.id === orderInfoSummaryBodyIds.fob ? newValue ?? '' : order.fob,
            requiredDate: entry.id === orderInfoSummaryBodyIds.requiredDate ? newValue ?? null : order.requiredDate,
            referenceNo: entry.id === orderInfoSummaryBodyIds.referenceNo ? newValue ?? '' : order.referenceNo,
            territoryCode: entry.id === orderInfoSummaryBodyIds.territory ? newValue ?? '' : order.territory,
            accountManagerCode: entry.id === orderInfoSummaryBodyIds.accountManager ? newValue?.code ?? null : order.accountManager?.code,
            paymentTerms: entry.id === orderInfoSummaryBodyIds.termsCode ? newValue ?? '' : order.termsCode,
        };
        if (entry.id === orderInfoSummaryBodyIds.requiredDate && !newValue) {
            newValue = order.orderDate;
        }
        setSubmittingDisabled(true);
        const response = await Api.updateSalesOrdersPOAndFob(forApi);
        if (!isMounted())
            return;
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            setOrder(prevState => ({...prevState, [name]: newValue}));
            if (entry.id === orderInfoSummaryBodyIds.termsCode) {
                // fetch the order silently to update the general information.
                void getOrderInformation();
            }
        }
        return response?.resultFlag;
    }, [customerId, orderId, order.customerPO, order.fob, order.requiredDate, order.referenceNo, order.territory, order.accountManager, order.termsCode, order.orderDate, isMounted, setOrder, getOrderInformation])

    /**
     * Update the shipping information of the current order.
     * @type {function(*=): Promise<void>}
     */
    const updateOrdersShippingInformation = useCallback(async (values) => {
        if (!operatorAccessLevels.packages.sales.manageSalesOrders || !operatorAccessLevels.packages.sales.orderFreightInfo) {
            return;
        }
        const forApi = {
            orderId: order.id,
            freightCompanyInfo: values?.freightCompany,
            freightFee: Number(values?.customerFee),
            trackingNo: values?.trackingNo,
            estimatedDeliveryDateTime: values?.estimatedDate,
            estimatedFee: Number(values?.estimatedFee),
            freightInfoId: shippingInfo.id,
            freeFreightId: shippingInfo.freeFreightId,
            shippingBillTo: values?.shippingBillTo ?? null,
            metaData: values?.metaData ?? null,
        }
        setSubmittingDisabled(true);
        const response = await Api.saveOrderFreightInfo(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            if (forApi.freightFee !== Number(shippingInfo.customerFee)) {
                await getOrderInformation();
            } else {
                setShippingInfo(prevState => ({
                    ...prevState,
                    id: prevState.id ?? response.data?.id,
                    freightCompany: values?.freightCompany,
                    estimatedDate: values?.estimatedDate,
                    estimatedFee: Number(values?.estimatedFee),
                    customerFee: Number(values?.customerFee),
                    trackingNo: values?.trackingNo,
                    shippingBillTo: values?.shippingBillTo ?? null,
                    metaData: values?.metaData ?? null,
                }))
                shippingInfoCompanyIdRef.current = values?.freightCompany?.id;
            }
        }
        setSubmittingDisabled(false);
    }, [order.id, shippingInfo.id, shippingInfo.freeFreightId, shippingInfo.customerFee, isMounted, getOrderInformation, operatorAccessLevels.packages.sales.manageSalesOrders, operatorAccessLevels.packages.sales.orderFreightInfo])

    /**
     * Updates the freight company of the order and refetches the free shipping eligibility and freight estimation based on the new freight company.
     * @type {(function(*): void)|*}
     */
    const onFreightCompanyChanged = useCallback((freightCompany) => {
        shippingInfoCompanyIdRef.current = freightCompany?.id;
    }, [])

    /**
     * Reverts this order back to its previous entity in the server.
     * - if the result is successful, navigates the operator back to the previous entity's information view.
     * @type {function(): Promise<void>}
     */
    const revertToPreviousOrderEntity = useCallback(async () => {
        setReverting(true);
        setSubmittingDisabled(true);
        const response = await Api.revertToQuoteByCustomer(customerId, orderId);
        if (!isMounted())
            return;
        setReverting(false);
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            history.replace(routeFunctions.customer.sales.quotes.single(customerId, orderId));
        }
    }, [customerId, orderId, isMounted, history])

    /**
     * Removes the selected transaction from the order
     *
     * - if the result of the api is successful, removes the transaction from transactions list of the order.
     * @type {function(*=): Promise<void>}
     */
    const removeTransactionFromOrder = useCallback(async (transactionId) => {
        const numericTransactionId = Number(transactionId);
        if (!Number.isFinite(numericTransactionId)) {
            ToastService.error({
                title: 'Refund failed',
                message: 'The transaction could not be refunded because its identifier is invalid.',
            });
            return;
        }
        const forApi = {
            transactionId: numericTransactionId,
            orderId: orderId,
            customerId: customerId,
        }
        setSubmittingDisabled(true);
        const response = await Api.removeSalesOrderTransactionItem(forApi);
        if (!isMounted())
            return;
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            setOrder(response.data);
        }
    }, [orderId, customerId, isMounted, setOrder])

    /**
     * Sends an email of this order's information to the provided email address of the user by calling its appropriate
     * api.
     * @type {function(*, *): Promise<boolean|undefined>}
     */
    const sendEmail = useCallback(async (emailAddress, includeBCC) => {
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            sendBccToOperator: includeBCC,
            email: emailAddress,
        };
        const response = await Api.sendSalesOrderInformationToUserEmail(forApi);
        return response?.resultFlag;
    }, [customerId, orderId])

    /**
     * Pays for a current order with the provided values.
     * @type {function(*): Promise<boolean|undefined>}
     */
    const payOrder = useCallback(async (values) => {
        let forApi = {
            ...values,
            customerId: customerId,
            orderId: orderId
        };
        setSubmittingDisabled(true);
        const response = await Api.paySalesOrder(forApi);
        if (!isMounted())
            return [];
        setSubmittingDisabled(false);
        const sendEmailConfirmation = {
            send: sendEmail,
        };
        if (response?.resultFlag) {
            setOrder(response.data);
        }
        return [
            response?.resultFlag ?? false,
            sendEmailConfirmation,
        ]
    }, [customerId, isMounted, orderId, sendEmail, setOrder])

    /**
     * Generates the next order entity from this entity in the server.
     *
     * - if the result of the api is successful navigates the user to the next order entity's information view.
     * - if errorCode is [ApiResponseCodes.preAuthorizedPaymentsFailed] then fills the failedPreAuthPayments list to
     * save the failed payments for showing.
     * @param {boolean} backorderPermission whether to allow backorder to be submitted for this order or not.
     * @type {function(*=): Promise<void>}
     */
    const generateNextOrderEntity = useCallback(async (backorderPermission = false) => {
        const forApi = {
            isBackOrderRequired: backorderPermission,
            customerId: customerId,
            orderId: orderId,
        }
        const response = await Api.generateInvoice(forApi);
        if (!isMounted())
            return
        if (response?.resultFlag) {
            return history.replace(routeFunctions.customer.sales.invoices.single(customerId, response.data.invoiceId));
        }
        if (response?.errorCode === ApiResponseCodes.preAuthorizedPaymentsFailed) {
            const failedAuthCodes = response?.data?.listOfFailedPreAuthorizedPayments?.map(e => e.authCode) ?? [];
            setFailedPreAuthPayments(order?.appliedTransactions?.filter(e => failedAuthCodes.includes(e.authorizationCode)) ?? []);
            setAttemptedInvoiceGeneration(true);
            getOrderInformation(false).then();
        }
    }, [customerId, getOrderInformation, history, isMounted, order?.appliedTransactions, orderId])

    /**
     * Depending on whether there are backorder items, shows the backorder dialog, otherwise generates the next
     * order entity.
     */
    const openBackorderDialogOrGenerateNextEntity = useCallback(async () => {
        let hasBackorder = false;
        for (let i = 0; i < order.items?.length ?? 0; i++) {
            const backorderQuantity = operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders ? parseFloat(order.items[i].backorderQty) : parseInt(order.items[i].backorderQty);
            if (backorderQuantity) {
                hasBackorder = true;
                break;
            }
        }
        if (!hasBackorder) {
            await generateNextOrderEntity();
        } else {
            setOpenBackorderConfirmationDialog(true);
        }
    }, [generateNextOrderEntity, order.items, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders])

    /**
     * Updates the address of this order in the server.
     *
     * - If the result of the api is successful, sets the new address information in the order state.
     * @type {function(*): Promise<boolean|undefined>}
     */
    const updateAddress = useCallback(async (address) => {
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            address: address,
        }
        setSubmittingDisabled(true);
        const response = await Api.updateSalesOrderAddress(forApi);
        if (!isMounted())
            return;
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            setOrder(response.data);
        }
        return response?.resultFlag;
    }, [customerId, orderId, isMounted, setOrder])

    /**
     * Changes the tab of the view.
     * @type {function(*=, *=): void}
     */
    const onTabChange = useCallback((_, newValue) => {
        setQuery(prevState => ({...prevState, tab: newValue}));
    }, [setQuery]);

    /**
     * Opens the send-email-dialog.
     * @type {function(): void}
     */
    const openEmailConfirmation = useCallback(() => {
        setSendOrderEmailDialogState({
            open: true,
        });
    }, [])

    /**
     * Gets users confirmation before reverting the current order entirely.
     */
    const getRevertConfirmation = useCallback(() => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    By reverting this order to a quote, all of its committed quantities and selected serial numbers will
                    be removed. Do you want to proceed?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && revertToPreviousOrderEntity(),
        );
    }, [revertToPreviousOrderEntity])

    const orderInfoSummary = useMemo(() => ({
        header: {
            title: 'Order No',
            value: order?.orderNo,
            loadingValue: "Loading...",
            copyable: true,
        },
        body: [
            ...(
                order?.isBackOrder
                && operatorAccessLevels.packages.sales.invoiceInformation
                    ? [{
                        id: orderInfoSummaryBodyIds.invoice,
                        title: 'Invoice',
                        titleSuffix:
                            <IconButton
                                disabled={loading}
                                className={'mx-2'}
                                color={'secondary'}
                                {...(order?.backOrderRelatedInvoiceInfo?.invoiceId && {
                                    component: Link,
                                    to: routeFunctions.customer.sales.invoices.single(customerId, order?.backOrderRelatedInvoiceInfo?.invoiceId),
                                })}
                            >
                                <OpenInNew className={'icon small'}/>
                            </IconButton>,
                        value: order?.backOrderRelatedInvoiceInfo?.invoiceNo,
                    }]
                    : []
            ),
            {
                id: orderInfoSummaryBodyIds.termsCode,
                title: 'Payment Terms',
                value: order?.termsCode,
                editable: EnvService.DEV_editOrderTermsCode && !!order && !order.isClosed && operatorAccessLevels.packages.sales.manageSalesOrders,
                edit: updateOrderInfo,
                openDialog: true,
            },
            {
                id: orderInfoSummaryBodyIds.territory,
                title: 'Territory',
                value: order?.territory,
                editable: !!order && operatorAccessLevels.packages.base.manageTerritories,
                clearable: !!order,
                edit: updateOrderInfo,
                openDialog: true,
            },
            {
                id: orderInfoSummaryBodyIds.salesPerson,
                title: 'Sales person',
                value: order?.salesperson?.code,
            },
            {
                id: orderInfoSummaryBodyIds.accountManager,
                title: 'Account manager',
                value: order?.accountManager?.code,
                editable: !!order && operatorAccessLevels.packages.base.manageTerritories,
                clearable: !!order,
                edit: updateOrderInfo,
                openDialog: true,
            },
            {
                id: orderInfoSummaryBodyIds.orderDate,
                title: 'Order date',
                value: order?.orderDate,
                format: (val) => val ? DateUtils.serverToLocal(val).format(DateUtils.dateFormat) : '--',
            },
            {
                id: orderInfoSummaryBodyIds.requiredDate,
                title: 'Required date',
                value: order?.requiredDate,
                format: (val) => val ? DateUtils.serverToLocal(val).format(DateUtils.dateFormat) : '--',
                type: summaryInfoCardBodyEntryTypes.date,
                editable: !!order && !order.isClosed && operatorAccessLevels.packages.sales.manageSalesOrders,
                clearable: !!order && !order.isClosed,
                edit: updateOrderInfo,
            },
            {
                id: orderInfoSummaryBodyIds.type,
                title: 'Type',
                value: order?.type,
            },
            {
                id: orderInfoSummaryBodyIds.status,
                title: 'Status',
                value: order?.status,
            },
            {
                id: orderInfoSummaryBodyIds.poNo,
                title: 'Po number',
                value: order?.customerPO,
                editable: !!order && !order.isClosed && operatorAccessLevels.packages.sales.manageSalesOrders,
                clearable: !!order && !order.isClosed,
                edit: updateOrderInfo,
            },
            {
                id: orderInfoSummaryBodyIds.fob,
                title: 'Fob',
                value: order?.fob,
                editable: !!order && !order.isClosed && operatorAccessLevels.packages.sales.manageSalesOrders,
                clearable: !!order && !order.isClosed,
                edit: updateOrderInfo,
            },
            {
                id: orderInfoSummaryBodyIds.referenceNo,
                title: 'Reference No',
                value: order?.referenceNo,
                editable: order && !order.isClosed && operatorAccessLevels.packages.sales.manageSalesOrders,
                clearable: order && !order.isClosed,
                edit: updateOrderInfo,
            },
        ],
    }), [order, operatorAccessLevels.packages.sales.invoiceInformation, operatorAccessLevels.packages.sales.manageSalesOrders, operatorAccessLevels.packages.base.manageTerritories, loading, customerId, updateOrderInfo])

    const userInfoSummary = useMemo(() => ({
        header: {
            title: 'Customer',
            value: accountInfo?.customer?.name,
            loadingValue: "Loading...",
        },
        body: [
            {
                id: userInfoSummaryBodyIds.accountReceivable,
                title: 'Account receivable',
                value: accountInfo?.accountReceivable,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.limitedCredit,
                title: 'Credit limit',
                value: accountInfo?.creditLimit,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.otherOrders,
                title: 'Other orders',
                value: accountInfo?.otherOrders,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.availableCredit,
                title: 'Available credit',
                value: accountInfo?.availableCredit,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.priceLevel,
                title: 'Price level',
                value: accountInfo?.priceLevel?.title,
            },
            {
                id: userInfoSummaryBodyIds.paymentTerms,
                title: 'Payment terms',
                value: accountInfo?.paymentTermsCode,
            },
        ],
    }), [accountInfo])


    /**
     * Opens the print configuration dialog.
     * @type {(function(): void)}
     */
    const openPrintConfigDialog = useCallback(() => {
        setPrintOrderConfigurationsDialog({
            open: true,
            printUrl: routeFunctions.print.salesOrder(customerId, orderId),
            onClose: () => setPrintOrderConfigurationsDialog(initPrintOrderConfigurationsDialogState)
        })
    }, [customerId, orderId])

    return (
        <>
            <div className='order-information-view sales-views-top-progress-indicator-offset'>
                {
                    (!loading && !!order?.isDeleted) &&
                    <>
                        <Col className={'h-100 w-100 align-items-center justify-content-center d-flex flex-column'}>
                            <EmptyCart className={'svg'} style={{width: 'min(500px, 90vw)'}}/>
                            <p className='text-sm text-color-1 text-center'>
                                The selected Order has been removed. In case of any problems, please contact our technical support.
                            </p>
                            <div className='w-100 my-3'/>
                            {
                                operatorAccessLevels.packages.sales.salesOrdersList &&
                                <Button
                                    variant={'outlined'}
                                    color={'primary'}
                                    component={Link}
                                    to={
                                        order?.isBackOrder
                                            ? routeFunctions.customer.sales.backOrder.base(customerId)
                                            : routeFunctions.customer.sales.salesOrder.base(customerId)
                                    }
                                >
                                    See {defaultCustomer?.name}'s Orders
                                </Button>
                            }
                        </Col>
                    </>
                }
                {
                    !(!loading && !!order?.isDeleted) &&
                    <>
                        <Row className='no-gutters w-100 align-items-center justify-content-between'>
                            <Row className='justify-content-start align-items-center no-gutters'>
                                <Tooltip
                                    title={`Back to ${defaultCustomer?.name}'s ${order?.isBackOrder ? 'back-orders' : 'sales orders'}`}>
                                    <Box component={'span'}>
                                        <IconButton
                                            color={'paper.secondary'}
                                            component={Link}
                                            to={order?.isBackOrder ? routeFunctions.customer.sales.backOrder.base(customerId) : routeFunctions.customer.sales.salesOrder.base(customerId)}
                                            replace
                                        >
                                            <ArrowBack className='icon normal'/>
                                        </IconButton>
                                    </Box>
                                </Tooltip>
                                <Typography variant={'h6'} fontWeight={600} marginInlineStart={1}>
                                    {order?.isBackOrder ? 'Back Order' : 'Sales Order'} Information
                                </Typography>
                            </Row>
                            <Row className='justify-content-end align-items-center no-gutters' style={{gap: 8}}>
                                {
                                    operatorAccessLevels.packages.sales.createOrderFromAnother &&
                                    <Tooltip arrow title={'Duplicate this Order'}>
                                        <IconButton
                                            onPressed={() => setDuplicateDialogOpen(true)}
                                            color={'paper.secondary'}
                                        >
                                            <FileCopyOutlined className={'icon large'}/>
                                        </IconButton>
                                    </Tooltip>
                                }
                                <Tooltip arrow title={'Send order information in an email'}>
                                    <IconButton
                                        onPressed={openEmailConfirmation}
                                        color={'paper.secondary'}
                                        className={'email-icon-container'}
                                    >
                                        <MailOutline className='icon large'/>
                                    </IconButton>
                                </Tooltip>
                                {
                                    operatorAccessLevels.packages.bizPrint.printSalesOrder &&
                                    <Popover
                                        child={(onEnter) => (
                                            <Tooltip arrow title={'Print Order'}>
                                                <IconButton
                                                    onPressed={onEnter}
                                                    color={'paper.secondary'}
                                                >
                                                    <SvgIcon className='icon large' component={Printer}/>
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        popoverChild={() => (
                                            <Row
                                                className='no-gutters align-items-center justify-content-center px-2'
                                                style={{maxWidth: '13rem'}}>
                                                <Col xs={12} className={'mx-2 my-2'}>
                                                    <Button
                                                        color={'secondary'}
                                                        variant={'text'}
                                                        className={'w-100'}
                                                        component={Link}
                                                        target={'_blank'}
                                                        to={stringifyUrl({
                                                            url: routeFunctions.print.salesOrder(params.customerId, params.orderId),
                                                            query: {showAddresses: true, showPaymentTerms: true, showPrices: true, showTransactions: false}
                                                        })}
                                                    >
                                                        Without transactions
                                                    </Button>
                                                </Col>
                                                <Col xs={12} className={'mx-2 mb-2'}>
                                                    <Button
                                                        color={'secondary'}
                                                        variant={'text'}
                                                        className={'w-100'}
                                                        component={Link}
                                                        target={'_blank'}
                                                        to={stringifyUrl({
                                                            url: routeFunctions.print.salesOrder(params.customerId, params.orderId),
                                                            query: {showAddresses: true, showPaymentTerms: true, showPrices: true, showTransactions: true}
                                                        })}
                                                    >
                                                        With transactions
                                                    </Button>
                                                </Col>
                                                <Col xs={12} className={'mx-2 mb-2'}>
                                                    <Button
                                                        color={'secondary'}
                                                        variant={'text'}
                                                        className={'w-100'}
                                                        onClick={openPrintConfigDialog}
                                                    >
                                                        Custom
                                                    </Button>
                                                </Col>
                                            </Row>
                                        )}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'right',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                    />
                                }
                                {
                                    !order?.isClosed && operatorAccessLevels.packages.sales.manageSalesOrders &&
                                    <>
                                        <Tooltip arrow title={'Revert to quote'}>
                                            <IconButton
                                                onPressed={getRevertConfirmation}
                                                color={'paper.error'}
                                                disabled={reverting}
                                            >
                                                {
                                                    reverting
                                                        ? <CircularProgress size={20} color={'secondary'}/>
                                                        : <SettingsBackupRestore className='icon large'/>
                                                }
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                }
                            </Row>
                        </Row>
                        <div className='w-100 my-3'/>
                        <div className='d-flex flex-wrap w-100 justify-content-start'>
                            <Col xs={12} md={6} xl={4} className={'mb-3'}>
                                <SummaryInfoCard
                                    header={orderInfoSummary.header}
                                    body={orderInfoSummary.body}
                                    loading={loading}
                                    loadingCount={orderInfoSummary.body.length}
                                />
                            </Col>
                            <Col xs={12} md={6} xl={4} className={'mb-3'}>
                                <SummaryInfoCard
                                    header={userInfoSummary.header}
                                    body={userInfoSummary.body}
                                    loading={accountInfoLoading}
                                    loadingCount={userInfoSummary.body.length}
                                />
                            </Col>
                            <Col xs={12} md={6} xl={4} className={'mb-3'} style={{overflowY: 'hidden'}}>
                                <SalesOrderInformationViewBillInfoSection
                                    order={order}
                                    loading={loading}
                                    generateNextOrderEntity={openBackorderDialogOrGenerateNextEntity}
                                    removeTransaction={removeTransactionFromOrder}
                                    getOrderInformation={getOrderInformation}
                                    payOrder={payOrder}
                                    customerId={customerId}
                                    failedPreAuthPayments={failedPreAuthPayments}
                                    attemptedInvoiceGeneration={attemptedInvoiceGeneration}
                                    submittingDisabled={submittingDisabled}
                                />
                            </Col>
                            {
                                operatorAccessLevels.packages.sales.orderFreightInfo &&
                                <Col xs={12} md={6} xl={8} className={'mb-3'}>
                                    <OrderInformationViewShippingInfoCard
                                        loading={loading || loadingShippingInfo}
                                        shippingInfo={shippingInfo}
                                        freeShippingEligibility={freeShippingEligibility}
                                        getFreeShippingEligibility={getFreeShippingEligibility}
                                        getFreightEstimation={getFreightEstimation}
                                        freightEstimation={freightEstimation}
                                        loadingFreightEstimation={loadingFreightEstimation}
                                        viewOnly={!operatorAccessLevels.packages.sales.manageSalesOrders}
                                        update={operatorAccessLevels.packages.sales.manageSalesOrders
                                            ? updateOrdersShippingInformation
                                            : undefined}
                                        onFreightCompanyChanged={onFreightCompanyChanged}
                                        customerId={customerId}
                                    />
                                </Col>
                            }
                        </div>
                        <div className='w-100 my-4'/>
                        <div className='w-100 flex-grow-1'>
                            <Tabs value={tab} onChange={onTabChange}>
                                {
                                    tabsList.map(tab => (
                                        <Tab
                                            key={tab.value}
                                            label={tab.label}
                                            value={tab.value}
                                        />
                                    ))
                                }
                            </Tabs>
                        </div>
                        <div className='my-3 w-100'/>
                        {
                            tab === tabs.products &&
                            <SalesOrderInformationViewProductsSection
                                order={order}
                                setOrder={setOrder}
                                orderLoading={loading}
                                setSubmittingDisabled={setSubmittingDisabled}
                            />
                        }
                        {
                            tab === tabs.billingAddress &&
                            <OrderInformationViewEditAddressesSection
                                address={order?.billingAddress}
                                updateAddress={updateAddress}
                                loading={loading}
                                typeId={CustomerAddressTypes.billing}
                                customerId={customerId}
                                customerName={defaultCustomer?.name}
                            />
                        }
                        {
                            tab === tabs.shippingAddress &&
                            <OrderInformationViewEditAddressesSection
                                address={order?.shippingAddress}
                                updateAddress={updateAddress}
                                onAddressUpdated={getFreightEstimation}
                                loading={loading}
                                typeId={CustomerAddressTypes.shipping}
                                customerId={customerId}
                                customerName={defaultCustomer?.name}
                            />
                        }
                        {
                            tab === tabs.addresses &&
                            <OrderInformationViewReadOnlyAddressesSection
                                billing={order?.billingAddress}
                                shipping={order?.shippingAddress}
                                loading={loading}
                            />
                        }
                        {
                            tab === tabs.transactions &&
                            <OrderInformationViewTransactionsSection
                                appliedTransactions={order?.appliedTransactions}
                                loading={loading}
                                orderId={orderId}
                                customerId={customerId}
                                type={OrderInformationViewTransactionsTypes.order}
                            />
                        }
                        {
                            tab === tabs.notes &&
                            <OrderInformationViewNotesSection
                                orderNo={order?.orderNo}
                                loading={loading}
                                placeType={OrderNoteAutoDefineTypes.salesOrder}
                                editable={operatorAccessLevels.packages.sales.manageSalesOrders}
                            />
                        }
                        {
                            tab === tabs.dimensions && operatorAccessLevels.packages.sales.salesPallets &&
                            <OrderDimensionsSection
                                orderId={orderId}
                                loading={loading}
                                items={order?.items}
                            />
                        }
                        <div className='my-3 w-100'/>
                    </>
                }
            </div>
            <BackOrderGenerationConfirmationDialog
                open={openBackorderConfirmationDialog}
                close={() => setOpenBackorderConfirmationDialog(false)}
                items={order?.items ?? []}
                onAccept={generateNextOrderEntity}
            />
            <SendOrderEmailDialog
                open={sendOrderEmailDialogState.open}
                close={() => setSendOrderEmailDialogState(initSendOrderEmailDialogState)}
                send={sendEmail}
                customerId={customerId}
            />
            <PrintOrderConfigurationsDialog
                open={printOrderConfigurationsDialog.open}
                onClose={printOrderConfigurationsDialog.onClose}
                printUrl={printOrderConfigurationsDialog.printUrl}
            />
            <UpsertOrderInfoDialog
                open={upsertOrderInfoDialog.open}
                close={upsertOrderInfoDialog.close}
                entityToShow={upsertOrderInfoDialog.entityToShow}
                update={upsertOrderInfoDialog.update}
                salesPerson={upsertOrderInfoDialog.salesPerson}
                territory={upsertOrderInfoDialog.territory}
                accountManager={upsertOrderInfoDialog.accountManager}
                termsCode={upsertOrderInfoDialog.termsCode}
            />
            {
                operatorAccessLevels.packages.sales.createOrderFromAnother &&
                <DuplicateOrderDialog
                    recordId={orderId}
                    customerId={customerId}
                    variant={DuplicateOrderVariants.SalesOrders}
                    open={duplicateDialogOpen}
                    close={() => setDuplicateDialogOpen(false)}
                />
            }
        </>
    );
}

export default SalesOrderInformationView;
